// KHD custom JS. Loaded as an ES module after Boost's theme.js via layout/theme.liquid.
// Keep all KHDStickers-specific behaviour in this file so Boost upstream files stay untouched.
// Each ported feature appends its own block below.

// --- Sidebar nav: submenu toggle + header height measurement ---
// Click delegate expands/collapses submenus in khd-sidebar-nav.
// The sidebar's top offset depends on the static header height; we measure
// it once on load + on resize and expose it as --khd-header-height so the
// CSS rule (top: var(--khd-header-height, 80px)) stays accurate.
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.khd-sidebar__toggle');
  if (!toggle) return;

  const controlsId = toggle.getAttribute('aria-controls');
  if (!controlsId) return;

  const submenu = document.getElementById(controlsId);
  if (!submenu) return;

  const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!isExpanded));
  submenu.toggleAttribute('hidden', isExpanded);
});

// Header height is exposed as --khd-header-height for any KHD element that
// needs to align below the header (currently the sidebar; future features
// may use it too). Runs on every page, not just sidebar-enabled ones, so
// the variable is always available.
(() => {
  const updateHeaderHeight = () => {
    // Measure the outer .header-group wrapper (announcement bar + search +
    // nav rows), not just .site-header — Boost stacks multiple sub-rows.
    const headerGroup = document.querySelector('.header-group');
    if (!headerGroup) return;
    const height = headerGroup.getBoundingClientRect().height;
    if (height > 0) {
      document.documentElement.style.setProperty('--khd-header-height', `${Math.round(height)}px`);
    }
  };

  updateHeaderHeight();
  window.addEventListener('resize', updateHeaderHeight, { passive: true });
  // Re-measure when fonts load (Boost loads custom fonts which can shift
  // header height after initial paint).
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(updateHeaderHeight);
  }
})();

// --- Back-to-Top button ---
// Injects a floating button that appears after scrolling past THRESHOLD px
// and smooth-scrolls to the top on click. No Liquid/HTML edits needed.
(() => {
  const THRESHOLD = 600;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'khd-back-to-top';
  btn.setAttribute('aria-label', 'Back to top');
  btn.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<polyline points="18 15 12 9 6 15"></polyline>' +
    '</svg>';

  btn.addEventListener('click', () => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
  });

  document.body.appendChild(btn);

  let ticking = false;
  const update = () => {
    btn.classList.toggle('khd-back-to-top--visible', window.scrollY > THRESHOLD);
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });
})();

// --- Font selector: enforce "required, no default" on add-to-cart ---
// Boost's AJAX add-to-cart (theme.js) serialises the form WITHOUT calling
// checkValidity(), so a native `required` attribute does not block submission.
// The font dropdown (snippets/khd-font-selector.liquid) is a Boost custom-select
// whose hidden <select name="properties[Font]"> is associated to the product form
// via the form= attribute (it lives OUTSIDE the <form> in the DOM). We guard it
// with a delegated, capture-phase submit listener so this runs BEFORE Boost's
// bubble-phase jQuery handler; stopImmediatePropagation() then prevents the add.
(() => {
  const findFontSelectFor = (form) => {
    const wraps = document.querySelectorAll('[data-khd-font-required]');
    for (const wrap of wraps) {
      const sel = wrap.querySelector('select.custom-select__native') || wrap.querySelector('select');
      // sel.form resolves the form= association even though sel is outside <form>.
      if (sel && sel.form === form) return { wrap, sel };
    }
    return null;
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const match = findFontSelectFor(form);
      if (!match) return;

      const { wrap, sel } = match;
      const error = wrap.querySelector('.khd-font-error');

      if (!sel.value) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (error) error.hidden = false;
        // Re-enable the add button if Boost's handler already disabled it.
        form.querySelectorAll('[data-add-to-cart], :submit').forEach((b) => b.removeAttribute('disabled'));
        // custom-select's visible trigger is `${baseId}-button`; sel.id is `${baseId}-native`.
        const btn = document.getElementById(`${sel.id.replace(/-native$/, '')}-button`);
        (btn || sel).focus?.({ preventScroll: true });
        wrap.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } else if (error) {
        error.hidden = true;
      }
    },
    true // capture phase
  );

  // Clear the error as soon as a valid font is chosen.
  document.addEventListener('change', (event) => {
    const sel = event.target;
    if (!(sel instanceof HTMLSelectElement) || sel.name !== 'properties[Font]') return;
    const wrap = sel.closest('[data-khd-font-required]');
    const error = wrap && wrap.querySelector('.khd-font-error');
    if (error && sel.value) error.hidden = true;
  });
})();
